// Release 2.2B — deal enums + legacy mapping. Labels come from i18n via t().
//
// Backward compatibility: legacy stage/status/deal_type values from Release 2.2A
// stored in existing deals must continue to render. We map each legacy value to
// a translation key when possible, otherwise fall back to the raw string.

export const DEAL_TYPES_NEW = [
  "commodity_trade",
  "cross_border_supply",
  "shipping_chartering",
  "trade_finance_prep",
  "corporate_dd",
  "other",
];

// Legacy 2.2A deal_type strings kept for backward-compat display only.
export const DEAL_TYPES_LEGACY = [
  "Commercial Drafting",
  "Documentary Support",
  "Shipping & Chartering",
  "Management Desk",
];

const LEGACY_TYPE_LABEL = {
  "Commercial Drafting": "d_type_commercial",
  "Documentary Support": "d_type_documentary",
  "Shipping & Chartering": "d_type_shipping",
  "Management Desk": "d_type_management",
};

export function dealTypeLabelKey(v) {
  if (!v) return null;
  if (DEAL_TYPES_NEW.includes(v)) return `dt_${v}`;
  if (LEGACY_TYPE_LABEL[v]) return LEGACY_TYPE_LABEL[v];
  return null;
}

export const DEAL_STAGES_NEW = [
  "initial_enquiry",
  "counterparty_review",
  "negotiation",
  "offer_quotation",
  "contract_preparation",
  "payment_instrument",
  "pre_shipment",
  "shipment",
  "document_presentation",
  "completed",
  "cancelled",
];
export const DEAL_STAGES_LEGACY = ["intake", "drafting", "qa", "delivery"];

export function dealStageLabelKey(v) {
  if (!v) return null;
  if (DEAL_STAGES_NEW.includes(v)) return `ds_${v}`;
  if (DEAL_STAGES_LEGACY.includes(v)) return `d_stage_${v}`;
  return null;
}

export const DEAL_STATUSES_NEW = ["draft", "active", "on_hold", "needs_attention", "completed", "archived"];
export const DEAL_STATUSES_LEGACY = ["closed"];

export function dealStatusLabelKey(v) {
  if (!v) return null;
  return `dst_${v}`;
}

export const PARTY_ROLES = [
  "buyer", "seller", "broker", "intermediary", "supplier", "consignee",
  "notify_party", "carrier", "charterer", "shipowner", "bank",
  "inspection_company", "insurance_company", "other",
];

export function partyRoleLabelKey(v) {
  if (!v) return null;
  return `pr_${v}`;
}

/**
 * Deterministic missing-information check. Returns list of { key, fixTab }.
 * Rules based solely on stored data — no AI, no legal claims.
 */
export function computeMissingInfo(deal) {
  const p = deal?.parameters || {};
  const parties = deal?.parties || [];
  const findings = [];
  const has = (v) => v !== undefined && v !== null && String(v).trim() !== "";

  if (!has(deal?.title)) findings.push({ key: "mi_no_title", fixTab: "overview" });
  if (!has(deal?.deal_type)) findings.push({ key: "mi_no_type", fixTab: "overview" });

  const hasRole = (r) => parties.some((x) => x.role === r) || (r === "buyer" && has(deal?.counterparty));
  if (!hasRole("buyer")) findings.push({ key: "mi_no_buyer", fixTab: "parties" });
  if (!hasRole("seller")) findings.push({ key: "mi_no_seller", fixTab: "parties" });

  const goods = p.goods || p.product || {};
  if (!has(goods.commodity)) findings.push({ key: "mi_no_product", fixTab: "commercial" });
  if (!has(goods.quantity)) findings.push({ key: "mi_no_quantity", fixTab: "commercial" });
  if (!has(deal?.currency) && !has(goods.currency)) findings.push({ key: "mi_no_currency", fixTab: "commercial" });

  const dely = p.delivery || p.logistics || {};
  if (!has(dely.incoterms)) findings.push({ key: "mi_no_incoterms", fixTab: "delivery" });
  if (has(dely.incoterms) && !has(dely.named_place)) findings.push({ key: "mi_no_named_place", fixTab: "delivery" });
  if (!has(dely.shipment_window) && !has(dely.shipment_period)) findings.push({ key: "mi_no_delivery_window", fixTab: "delivery" });

  const pay = p.payment || {};
  if (!has(pay.method) && !has(pay.payment_instrument)) findings.push({ key: "mi_no_payment_method", fixTab: "payment" });
  const method = pay.method || pay.payment_instrument || "";
  const lcLike = /LC|SBLC|Documentary Collection|Letter of Credit/i.test(String(method));
  if (lcLike && !has(pay.issuing_bank)) findings.push({ key: "mi_no_issuing_bank", fixTab: "payment" });

  return findings;
}

/**
 * Extract lightweight display fields for the deals list.
 * Uses parties[] first, then falls back to legacy counterparty string.
 */
export function summariseDealForList(deal) {
  const parties = deal?.parties || [];
  const buyer = parties.find((p) => p.role === "buyer")?.legal_name || "";
  const seller = parties.find((p) => p.role === "seller")?.legal_name || "";
  const p = deal?.parameters || {};
  const goods = p.goods || p.product || {};
  const product = goods.commodity || goods.product || "";
  const quantity = goods.quantity || "";
  return {
    buyer: buyer || (deal?.counterparty && !seller ? deal.counterparty : ""),
    seller,
    product,
    quantity,
    currency: deal?.currency || goods.currency || "",
    total: deal?.total_value != null ? deal.total_value : null,
    nextActionTitle: deal?.next_action?.title || "",
  };
}

export const INCOTERMS_RULES = ["EXW", "FCA", "CPT", "CIP", "DAP", "DPU", "DDP", "FAS", "FOB", "CFR", "CIF"];
export const INCOTERMS_VERSIONS = ["Incoterms 2020", "Incoterms 2010"];
export const PAYMENT_METHODS = [
  "LC", "SBLC", "Bank Guarantee", "Documentary Collection", "Open Account",
  "Advance Payment", "Escrow", "Telegraphic Transfer", "Other",
];
export const CURRENCIES = ["USD", "EUR", "GBP", "CHF", "CNY", "AED", "JPY", "INR"];
export const TRANSPORT_MODES = ["Sea", "Road", "Rail", "Air", "Multimodal", "Inland waterway"];
export const QUANTITY_UNITS = ["MT", "kg", "L", "m³", "bbl", "pcs", "boxes", "containers"];

/**
 * Compute a lightweight activity list from stored deal + drafts without any
 * fabrication. If a dedicated activity log is added later, replace this.
 */
export function computeActivityEvents(deal, drafts) {
  const events = [];
  if (deal?.created_at) events.push({ at: deal.created_at, key: "ws_ev_created" });
  if (deal?.updated_at && deal.updated_at !== deal.created_at) {
    events.push({ at: deal.updated_at, key: "ws_ev_updated" });
  }
  if (deal?.archived_at) {
    events.push({ at: deal.archived_at, key: "ws_ev_archived" });
  }
  (drafts || []).forEach((d) => {
    events.push({
      at: d.created_at,
      key: "ws_ev_draft_linked",
      vars: { code: d.template_code || "DOC", v: d.version_number || 1 },
    });
    (d.workflow_history || []).forEach((event) => {
      events.push({
        at: event.created_at,
        key: "ws_ev_document_workflow",
        vars: {
          code: d.template_code || "DOC",
          status: event.to_status,
          actor: event.actor_name || event.actor_email || "—",
        },
      });
    });
  });
  return events.sort((a, b) => (b.at || "").localeCompare(a.at || ""));
}
